import React, { useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useIncidentObjectives } from '../hooks/incidentObjectiveHooks';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  IncidentObjective,
  useCreateIncidentObjective,
  useEditIncidentObjective,
} from '../hooks/incidentObjectiveHooks';
import { ObjectiveBase } from '../../../types/iapBaseTypes';
import PratusTextArea from 'components/DesignSystems/New/PratusTextArea';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import classNames from 'classnames';
import { useIncidentActions } from '../../Actions/hooks/incidentActionHooks';
import { AddActionCard, ShowActionCard } from './ActionCard';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import TimelineTable from './objectivesActionsViews/TimelineTable';
import AIReviewButton from 'components/DisasterGPT/AIReviewButton';

type ViewMode = 'management' | 'timeline';

const viewModeOptions = [
  { label: 'Management View', value: 'management' },
  { label: 'Timeline View', value: 'timeline' },
];

type SelectOptions = { value: string; label: string };

const actionOptions = [
  { value: 'Planned', label: 'Planned' },
  { value: 'Current/Ongoing', label: 'Current/Ongoing' },
  { value: 'Closed', label: 'Closed' },
];

const objectiveOptions = [
  { value: 'Operational', label: 'Operational' },
  { value: 'Management', label: 'Managerial' },
  {
    value: 'OperationalAndManagerial',
    label: 'Operational & Managerial',
  },
];

const filterSelectOptions = [
  { value: 'all', label: 'Select All' },
  { value: 'Operational', label: 'Operational' },
  { value: 'Management', label: 'Managerial' },
  {
    value: 'OperationalAndManagerial',
    label: 'Operational & Managerial',
  },
  { value: 'actions', label: 'Actions' },
];

const InitialResponseObjectivesActions: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('management');
  const [newAction, setNewAction] = useState<boolean>(false);
  const [newObjective, setNewObjective] = useState<ObjectiveBase | null>(null);
  const [addingAction, setAddingAction] = useState<string[]>([]);
  const [editing, setEditing] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOptions, setFilterOptions] = useState([filterSelectOptions[0]]);

  const { data: incidentObjectives } = useIncidentObjectives('incident');
  const { data: incidentActions } = useIncidentActions('incident');

  const incidentMutation = useEditIncidentObjective();
  const createIncidentMutation = useCreateIncidentObjective();

  const parentObjectiveOptions = [
    ...(incidentObjectives
      ?.filter((objective) => !objective.archived)
      .map((objective) => ({
        value: objective.objective_id,
        label: objective.name,
      })) || []),
    { value: '', label: 'No Objective' },
  ];

  const handleNewSave = async (e: string) => {
    const payload = { ...newObjective, name: e, description: e.slice(0, 20) };
    await createIncidentMutation.mutateAsync(payload);
    setNewObjective(null);
  };

  const handleAddObjective = () => {
    if (newObjective) setNewObjective(null);
    else {
      const emptyObjective: ObjectiveBase = {
        name: '',
        description: '',
        type: 'Operational',
      };
      setNewObjective(emptyObjective);
    }
  };

  const handleAddAction = (objectiveId: string) => {
    if (addingAction.includes(objectiveId))
      setAddingAction(addingAction.filter((id: string) => id !== objectiveId));
    else setAddingAction([...addingAction, objectiveId]);
  };

  const handleEdit = (objective: IncidentObjective) => {
    if (editing.includes(objective.objective_id))
      setEditing(editing.filter((id) => id !== objective.objective_id));
    else setEditing([...editing, objective.objective_id]);
  };

  const handleSave = (e: string, objective: IncidentObjective) => {
    incidentMutation.mutateAsync({
      ...objective,
      name: e,
    });
    setEditing(editing.filter((id) => id !== objective.objective_id));
  };

  const handleDelete = async (objective: IncidentObjective) => {
    await incidentMutation.mutateAsync({
      ...objective,
      archived: !objective.archived,
    });
  };

  const handleFilterChange = (newSelection: SelectOptions[]) => {
    if (!newSelection || newSelection.length === 0) {
      setFilterOptions([]);
      return;
    }

    const hasAll = newSelection.some((opt) => opt.value === 'all');
    const wasAll =
      filterOptions.length === 1 && filterOptions[0].value === 'all';
    const otherOptions = newSelection.filter((opt) => opt.value !== 'all');

    if (hasAll && wasAll && otherOptions.length > 0) {
      setFilterOptions(otherOptions);
      return;
    }

    if (hasAll && !wasAll) {
      setFilterOptions([filterSelectOptions[0]]);
      return;
    }

    if (otherOptions.length === 4) {
      setFilterOptions([filterSelectOptions[0]]);
      return;
    }

    setFilterOptions(otherOptions);
  };

  const filteredObjectives = incidentObjectives
    ?.filter((objective) => {
      const matchesSearch = objective.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const hasMatchingAction = incidentActions?.some(
        (action) =>
          action.parent_id === objective.objective_id &&
          !action.archived &&
          action.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

      const hasAll = filterOptions.some((opt) => opt.value === 'all');

      if (hasAll) {
        return matchesSearch || hasMatchingAction;
      }

      const matchesFilter = filterOptions.some(
        (opt) => objective?.type?.toLowerCase() === opt.value.toLowerCase()
      );

      return (matchesSearch || hasMatchingAction) && matchesFilter;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredIncidentActions = incidentActions
    ?.filter((action) => {
      const matchesSearch = action.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      // Check if "all" or "actions" is selected
      const hasAll = filterOptions.some((opt) => opt.value === 'all');
      const hasActions = filterOptions.some((opt) => opt.value === 'actions');

      if (hasAll || hasActions) {
        return matchesSearch;
      }

      return false;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const getObjectiveClass = (type?: string) => {
    if (type === 'Operational') return 'operational';
    if (type === 'OperationalAndManagerial')
      return 'operational-and-managerial';
    return 'managerial';
  };

  const getObjectiveLabel = (type?: string) => {
    if (type === 'Management') return 'Managerial';
    if (type === 'OperationalAndManagerial') return 'O&M';
    return type ?? '';
  };

  return (
    <div className="objectives-tab">
      <div className="bordered-card d-flex align-items-center justify-content-between mb-4 gap-2">
        <div className="d-flex align-items-center gap-2">
          <span>Objectives & Actions</span>
          <StylishNewSearchBar
            onChangeSearchTerm={(val) => setSearchTerm(val)}
            value={searchTerm}
            placeholder="Search"
            className="search-bar"
          />
          <StylishNewSelect
            onChange={handleFilterChange}
            isSearchable={false}
            value={filterOptions}
            isMulti={true}
            options={filterSelectOptions}
          />
        </div>
        <div className="d-flex align-items-center gap-2">
          <AIReviewButton actionKey="objectives" />
          <AIReviewButton actionKey="actions" />
          <StylishNewToggleButtonGroup
            data={viewModeOptions}
            selected={viewMode}
            onChange={(value: string) => setViewMode(value as ViewMode)}
          />
        </div>
      </div>
      {viewMode === 'timeline' && (
        <TimelineTable
          objectives={filteredObjectives ?? []}
          actions={filteredIncidentActions ?? []}
        />
      )}
      {viewMode === 'management' && (
        <div className="objectives-scroll-container">
          <div className="d-flex ms-2 mb-4 gap-4">
            <StylishNewButton
              onClick={handleAddObjective}
              className="ps-3 pe-4"
            >
              <SharedIcon iconName="add" classes="me-2" />
              Add Objective
            </StylishNewButton>
            <StylishNewButton
              onClick={() => setNewAction(!newAction)}
              className={classNames('gap-1 ps-3 pe-4', 'button--secondary')}
            >
              <SharedIcon iconName="add" classes="me-2" />
              Add Action
            </StylishNewButton>
          </div>
          {newObjective && (
            <div className="bordered-card d-flex align-items-flex-start">
              <StylishNewSelect
                defaultValue={objectiveOptions[0]}
                options={objectiveOptions}
                className="objectives-select"
                placeholder="Select type"
                onChange={(e) =>
                  setNewObjective({
                    ...newObjective,
                    type: e?.value,
                  })
                }
              />
              <div className="ms-2 flex-grow-1">
                <PratusTextArea
                  onSave={(e) => handleNewSave(e)}
                  onCancel={handleAddObjective}
                  maxLength={200}
                  minLength={1}
                  className={'text-area ms-2'}
                  placeholder="Enter objective text"
                />
              </div>
            </div>
          )}
          {newAction && (
            <AddActionCard
              objective={null}
              parentObjectiveOptions={parentObjectiveOptions}
              actionOptions={actionOptions}
              handleCancel={() => setNewAction(false)}
            />
          )}
          {filteredObjectives
            ?.filter((objective) => !objective.archived)
            .map((objective) => (
              <>
                <div
                  key={objective.objective_id}
                  className={
                    'bordered-card d-flex justify-content-between align-items-flex-start mb-2'
                  }
                >
                  <div className="d-flex align-items-start w-100">
                    <div className="d-flex align-items-start flex-grow-1 gap-2">
                      {editing.includes(objective.objective_id) ? (
                        <>
                          <StylishNewSelect
                            value={{
                              value: objective.type,
                              label:
                                objective.type === 'Management'
                                  ? 'Managerial'
                                  : objective.type ===
                                      'OperationalAndManagerial'
                                    ? 'Operational & Managerial'
                                    : objective.type,
                            }}
                            className="objectives-select"
                            options={objectiveOptions}
                            placeholder="Select type"
                            onChange={(e) => {
                              incidentMutation.mutateAsync({
                                ...objective,
                                type: e?.value as
                                  | 'Operational'
                                  | 'Management'
                                  | 'OperationalAndManagerial',
                              });
                            }}
                          />
                          <div className="ms-1 flex-grow-1">
                            <PratusTextArea
                              onSave={(e) => handleSave(e, objective)}
                              defaultValue={objective.name}
                              maxLength={200}
                              minLength={1}
                            />
                          </div>
                        </>
                      ) : (
                        <span className="d-flex align-items-center">
                          <span
                            className={classNames(
                              getObjectiveClass(objective.type),
                              'objective-icon me-2'
                            )}
                          >
                            {getObjectiveLabel(objective.type)}
                          </span>
                          <span className="item-name">{objective.name}</span>
                        </span>
                      )}
                    </div>
                    <div className="d-flex align-items-start ms-2 flex-shrink-0 flex-row gap-1">
                      <StylishNewButton
                        className="button--link action-button"
                        onClick={() => {
                          handleEdit(objective);
                        }}
                      >
                        <SharedIcon iconName="edit" size="sm" />
                      </StylishNewButton>
                      <StylishNewButton
                        className="button--link action-button"
                        disabled={incidentActions?.some(
                          (action) =>
                            !action.archived &&
                            action.parent_id === objective.objective_id
                        )}
                        onClick={() => {
                          handleDelete(objective);
                        }}
                      >
                        <SharedIcon iconName="delete" size="sm" />
                      </StylishNewButton>
                    </div>
                  </div>
                </div>

                <>
                  <div className="objective-card">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Aligned Actions</span>
                      <StylishNewButton
                        onClick={() => {
                          handleAddAction(objective.objective_id);
                        }}
                        className={classNames('button--secondary')}
                      >
                        <SharedIcon iconName="add" classes="me-2" />
                        Add Aligned Action
                      </StylishNewButton>
                    </div>
                    {addingAction.includes(objective.objective_id) && (
                      <AddActionCard
                        objective={objective}
                        actionOptions={actionOptions}
                        handleCancel={() =>
                          setAddingAction(
                            addingAction.filter(
                              (id: string) => id !== objective.objective_id
                            )
                          )
                        }
                      />
                    )}
                    {incidentActions
                      ?.filter(
                        (action) =>
                          action.parent_id === objective.objective_id &&
                          !action.archived
                      )
                      .filter((action) => {
                        const objectiveMatchesSearch = objective.name
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase());

                        if (!objectiveMatchesSearch && searchTerm) {
                          return action.name
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase());
                        }

                        return true;
                      })
                      .map((action, idx) => (
                        <ShowActionCard
                          action={action}
                          key={action.id}
                          index={idx}
                          objective={objective}
                          parentObjectiveOptions={parentObjectiveOptions}
                          actionOptions={actionOptions}
                        />
                      ))}
                  </div>
                </>
              </>
            ))}
          {filteredIncidentActions
            ?.filter((action) => action.parent_id === null && !action.archived)
            .map((action, idx) => (
              <ShowActionCard
                action={action}
                key={action.id}
                index={idx}
                parentObjectiveOptions={parentObjectiveOptions}
                actionOptions={actionOptions}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default InitialResponseObjectivesActions;
